/**
 * Validador de datos CSV para importación masiva
 */

/**
 * Valida los datos de productos
 */
export const validateProductData = (data) => {
  const errors = [];
  const warnings = [];

  data.forEach((row, index) => {
    const rowNumber = index + 2; // +2 por header y 0-index

    // Validar nombre_producto (requerido)
    if (!row.nombre_producto || row.nombre_producto.trim() === '') {
      errors.push({
        row: rowNumber,
        field: 'nombre_producto',
        message: 'El nombre del producto es requerido',
        type: 'error'
      });
    }

    // Validar descripcion_producto (opcional pero recomendado)
    if (!row.descripcion_producto || row.descripcion_producto.trim() === '') {
      warnings.push({
        row: rowNumber,
        field: 'descripcion_producto',
        message: 'Se recomienda agregar una descripción',
        type: 'warning'
      });
    }

    // Validar categoria (requerido)
    if (!row.categoria || row.categoria.trim() === '') {
      errors.push({
        row: rowNumber,
        field: 'categoria',
        message: 'La categoría es requerida',
        type: 'error'
      });
    }
  });

  return { errors, warnings, isValid: errors.length === 0 };
};

/**
 * Valida los datos de items
 */
export const validateItemData = (data) => {
  const errors = [];
  const warnings = [];

  data.forEach((row, index) => {
    const rowNumber = index + 2;

    // Producto: requerido por ID (Excel) o por nombre (CSV). id_product / id_producto o nombre_producto / producto
    const rawId = row.id_product ?? row.id_producto;
    const hasValidId = rawId !== '' && rawId !== undefined && rawId !== null && !Number.isNaN(Number(rawId));
    const nameStr = (row.nombre_producto ?? row.producto ?? '');
    const hasValidName = typeof nameStr === 'string' ? nameStr.trim() !== '' : String(nameStr || '').trim() !== '';
    if (!hasValidId && !hasValidName) {
      errors.push({
        row: rowNumber,
        field: 'id_product',
        message: 'Debe indicar id_product (Excel) o nombre del producto (CSV)',
        type: 'error'
      });
    }

    // Validar descripcion_item (requerido)
    const descStr = row.descripcion_item != null ? String(row.descripcion_item).trim() : '';
    if (descStr === '') {
      errors.push({
        row: rowNumber,
        field: 'descripcion_item',
        message: 'La descripción del ítem es requerida',
        type: 'error'
      });
    }

    // Validar precio (requerido y debe ser número) - puede venir como string o número del CSV
    const precioStr = row.precio != null ? String(row.precio).trim() : '';
    if (precioStr === '') {
      errors.push({
        row: rowNumber,
        field: 'precio',
        message: 'El precio es requerido',
        type: 'error'
      });
    } else if (isNaN(parseFloat(precioStr))) {
      errors.push({
        row: rowNumber,
        field: 'precio',
        message: 'El precio debe ser un número válido',
        type: 'error'
      });
    } else if (parseFloat(precioStr) < 0) {
      errors.push({
        row: rowNumber,
        field: 'precio',
        message: 'El precio no puede ser negativo',
        type: 'error'
      });
    }

    // Validar stock (requerido y debe ser número entero) - puede venir como string o número del CSV
    const stockStr = row.stock != null ? String(row.stock).trim() : '';
    if (stockStr === '') {
      errors.push({
        row: rowNumber,
        field: 'stock',
        message: 'El stock es requerido',
        type: 'error'
      });
    } else if (isNaN(parseInt(stockStr, 10))) {
      errors.push({
        row: rowNumber,
        field: 'stock',
        message: 'El stock debe ser un número entero',
        type: 'error'
      });
    } else if (parseInt(stockStr, 10) < 0) {
      errors.push({
        row: rowNumber,
        field: 'stock',
        message: 'El stock no puede ser negativo',
        type: 'error'
      });
    }

    // Validar SKU (opcional pero recomendado) - puede venir como string o número
    const skuStr = row.sku != null ? String(row.sku).trim() : '';
    if (skuStr === '') {
      warnings.push({
        row: rowNumber,
        field: 'sku',
        message: 'Se recomienda proporcionar un SKU',
        type: 'warning'
      });
    }
  });

  return { errors, warnings, isValid: errors.length === 0 };
};

/**
 * Valida los datos combinados (productos + items)
 */
export const validateCombinedData = (data) => {
  const errors = [];
  const warnings = [];
  const products = new Set();

  data.forEach((row, index) => {
    const rowNumber = index + 2;

    // Validar tipo (requerido)
    if (!row.tipo || !['PRODUCTO', 'ITEM'].includes(row.tipo.toUpperCase())) {
      errors.push({
        row: rowNumber,
        field: 'tipo',
        message: 'El tipo debe ser "PRODUCTO" o "ITEM"',
        type: 'error'
      });
      return;
    }

    const tipo = row.tipo.toUpperCase();

    // Validaciones específicas para PRODUCTO
    if (tipo === 'PRODUCTO') {
      if (!row.nombre_producto || row.nombre_producto.trim() === '') {
        errors.push({
          row: rowNumber,
          field: 'nombre_producto',
          message: 'El nombre del producto es requerido',
          type: 'error'
        });
      } else {
        products.add(row.nombre_producto);
      }

      if (!row.categoria || row.categoria.trim() === '') {
        errors.push({
          row: rowNumber,
          field: 'categoria',
          message: 'La categoría es requerida para productos',
          type: 'error'
        });
      }
    }

    // Validaciones específicas para ITEM
    if (tipo === 'ITEM') {
      if (!row.nombre_producto || row.nombre_producto.trim() === '') {
        errors.push({
          row: rowNumber,
          field: 'nombre_producto',
          message: 'El nombre del producto es requerido',
          type: 'error'
        });
      }

      if (!row.descripcion_item || row.descripcion_item.trim() === '') {
        errors.push({
          row: rowNumber,
          field: 'descripcion_item',
          message: 'La descripción del ítem es requerida',
          type: 'error'
        });
      }

      if (!row.precio || isNaN(parseFloat(row.precio))) {
        errors.push({
          row: rowNumber,
          field: 'precio',
          message: 'El precio debe ser un número válido',
          type: 'error'
        });
      }

      if (!row.stock || isNaN(parseInt(row.stock))) {
        errors.push({
          row: rowNumber,
          field: 'stock',
          message: 'El stock debe ser un número entero',
          type: 'error'
        });
      }
    }
  });

  return { errors, warnings, isValid: errors.length === 0 };
};

/**
 * Valida las columnas del CSV
 */
export const validateColumns = (headers, expectedColumns) => {
  const errors = [];
  const missingColumns = expectedColumns.filter(col => !headers.includes(col));
  const extraColumns = headers.filter(col => !expectedColumns.includes(col));

  if (missingColumns.length > 0) {
    errors.push({
      type: 'error',
      message: `Columnas faltantes: ${missingColumns.join(', ')}`
    });
  }

  if (extraColumns.length > 0) {
    errors.push({
      type: 'warning',
      message: `Columnas adicionales (serán ignoradas): ${extraColumns.join(', ')}`
    });
  }

  return { errors, isValid: missingColumns.length === 0 };
};

/**
 * Valida el tamaño del archivo
 */
export const validateFileSize = (file, maxSizeMB = 5) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `El archivo es demasiado grande. Tamaño máximo: ${maxSizeMB}MB`
    };
  }

  return { isValid: true };
};

/**
 * Valida el número de filas
 */
export const validateRowCount = (rowCount, maxRows = 1000) => {
  if (rowCount > maxRows) {
    return {
      isValid: false,
      error: `Demasiadas filas. Máximo permitido: ${maxRows} filas`
    };
  }

  return { isValid: true };
};
